import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReadContract, useReadContracts } from "wagmi";
import { type Address, formatUnits } from "viem";
import { Plus, ExternalLink, Droplets, ZoomIn, BarChart2, Loader2 } from "lucide-react";
import { ADDRESSES, V2_FACTORY_ABI, V2_PAIR_ABI, ERC20_ABI } from "../config/contracts";
import { CL_FEE_TIERS, BIN_STEPS } from "../config/v4Contracts";
import {
  NATIVE_JASMY,
  WJASMY_TOKEN,
  DJNN_TOKEN,
  BJCT_TOKEN,
  BBNB_TOKEN,
  BUSDT_TOKEN,
  BUSDC_TOKEN,
  JUSDT_TOKEN,
  FUD_TOKEN,
  BONK_TOKEN,
  FLUFF_TOKEN,
  SAMURAI_TOKEN,
  JBTC_TOKEN,
  MEOW_TOKEN,
  PAWS_TOKEN,
  PDAI_JASMY_TOKEN,
  PCOCK_TOKEN,
  XUSDP_TOKEN,
  type TokenInfo,
} from "../config/tokens";
import { explorerUrl, formatTokenAmount, cn } from "../lib/utils";

// Hook options for V4 pools (greyed out until deployed)
const AVAILABLE_HOOKS = [
  { label: "AutoCompound", coming: true },
  { label: "VortexPool", coming: true },
  { label: "MEV Recapture", coming: true },
] as const;

// Well-known pairs — DJNN pairs first
const KNOWN_PAIRS: Array<{ tokenA: TokenInfo; tokenB: TokenInfo }> = [
  // DJNN priority pairs
  { tokenA: NATIVE_JASMY, tokenB: DJNN_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: DJNN_TOKEN },
  { tokenA: DJNN_TOKEN, tokenB: PDAI_JASMY_TOKEN },
  { tokenA: DJNN_TOKEN, tokenB: PCOCK_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: PDAI_JASMY_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: XUSDP_TOKEN },
  // Standard pairs
  { tokenA: NATIVE_JASMY, tokenB: WJASMY_TOKEN },
  { tokenA: NATIVE_JASMY, tokenB: JUSDT_TOKEN },
  { tokenA: NATIVE_JASMY, tokenB: FUD_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: JUSDT_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: BONK_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: FLUFF_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: SAMURAI_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: JBTC_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: MEOW_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: PAWS_TOKEN },
  { tokenA: NATIVE_JASMY, tokenB: BJCT_TOKEN },
  { tokenA: NATIVE_JASMY, tokenB: BUSDT_TOKEN },
  { tokenA: NATIVE_JASMY, tokenB: BUSDC_TOKEN },
  { tokenA: NATIVE_JASMY, tokenB: BBNB_TOKEN },
  { tokenA: WJASMY_TOKEN, tokenB: BJCT_TOKEN },
  { tokenA: BUSDT_TOKEN, tokenB: BUSDC_TOKEN },
];

type PoolTab = "v2" | "cl" | "bin";

// ─── V2 Pool Row ──────────────────────────────────────────────────────────────
function V2PairRow({
  pairAddress,
  onAddLiquidity,
}: {
  pairAddress: Address;
  onAddLiquidity: () => void;
}) {
  const { data: token0 } = useReadContract({
    address: pairAddress,
    abi: V2_PAIR_ABI,
    functionName: "token0",
    query: { staleTime: Infinity },
  });
  const { data: token1 } = useReadContract({
    address: pairAddress,
    abi: V2_PAIR_ABI,
    functionName: "token1",
    query: { staleTime: Infinity },
  });
  const { data: reserves } = useReadContract({
    address: pairAddress,
    abi: V2_PAIR_ABI,
    functionName: "getReserves",
    query: { refetchInterval: 15_000 },
  });

  const { data: sym0 } = useReadContract({
    address: token0 as Address | undefined,
    abi: ERC20_ABI,
    functionName: "symbol",
    query: { enabled: !!token0, staleTime: Infinity },
  });
  const { data: sym1 } = useReadContract({
    address: token1 as Address | undefined,
    abi: ERC20_ABI,
    functionName: "symbol",
    query: { enabled: !!token1, staleTime: Infinity },
  });

  const reserveData = reserves as [bigint, bigint, number] | undefined;
  const r0 = reserveData?.[0] ?? 0n;
  const r1 = reserveData?.[1] ?? 0n;
  const tvlEstimate =
    r0 > 0n
      ? (parseFloat(formatUnits(r0, 18)) + parseFloat(formatUnits(r1, 18))).toFixed(2)
      : "—";

  if (!token0 || !token1) {
    return (
      <div className="flex items-center gap-3 p-4 bg-surface/30 rounded-button border border-border animate-pulse">
        <div className="h-4 w-32 bg-surface rounded" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-surface/30 rounded-button border border-border hover:border-border-active transition-all">
      <div className="flex items-center gap-3">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-jasmy-dim border-2 border-background flex items-center justify-center text-xs font-bold text-jasmy">
            {String(sym0 ?? "?").slice(0, 2)}
          </div>
          <div className="w-8 h-8 rounded-full bg-surface border-2 border-background -ml-2 flex items-center justify-center text-xs font-bold text-text-primary">
            {String(sym1 ?? "?").slice(0, 2)}
          </div>
        </div>
        <div>
          <div className="font-medium text-text-primary text-sm">
            {String(sym0 ?? "…")} / {String(sym1 ?? "…")}
          </div>
          <div className="text-xs text-text-tertiary mt-0.5">
            TVL: ~{tvlEstimate} tokens
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right hidden sm:block">
          <div className="text-xs text-text-tertiary">Earn trading fees</div>
          <div className="text-xs text-primary font-medium">0.25% / trade</div>
        </div>
        <a
          href={explorerUrl("address", pairAddress)}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 text-text-tertiary hover:text-text-primary transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <button onClick={onAddLiquidity} className="btn-primary py-1.5 px-3 text-sm flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </div>
    </div>
  );
}

// ─── Hook selector dropdown ───────────────────────────────────────────────────
function HookSelector() {
  return (
    <div className="relative">
      <select
        disabled
        className="appearance-none bg-surface/50 border border-border text-text-tertiary text-xs rounded-button px-3 py-1.5 cursor-not-allowed opacity-60"
        title="Hooks not yet deployed on JasmyChain"
      >
        <option>No Hooks</option>
        {AVAILABLE_HOOKS.map((h) => (
          <option key={h.label} disabled>{h.label} [coming soon]</option>
        ))}
      </select>
    </div>
  );
}

// ─── V4 Pool Row ──────────────────────────────────────────────────────────────
function V4PoolRow({
  tokenA,
  tokenB,
  badge,
  onAddLiquidity,
  showHooks,
}: {
  tokenA: TokenInfo;
  tokenB: TokenInfo;
  badge: string;
  onAddLiquidity: () => void;
  showHooks?: boolean;
}) {
  const symA = tokenA.symbol;
  const symB = tokenB.symbol;
  const isDJNNPair = symA === "DJNN" || symB === "DJNN";

  return (
    <div className={cn(
      "flex items-center justify-between p-4 rounded-button border transition-all",
      isDJNNPair
        ? "bg-primary/[0.04] border-primary/20 hover:border-primary/40"
        : "bg-surface/30 border-border hover:border-border-active"
    )}>
      <div className="flex items-center gap-3">
        <div className="flex items-center">
          <div className={cn(
            "w-8 h-8 rounded-full border-2 border-background flex items-center justify-center text-xs font-bold",
            isDJNNPair ? "bg-primary/20 text-primary" : "bg-jasmy-dim text-jasmy"
          )}>
            {symA.slice(0, 2)}
          </div>
          <div className="w-8 h-8 rounded-full bg-surface border-2 border-background -ml-2 flex items-center justify-center text-xs font-bold text-text-primary">
            {symB.slice(0, 2)}
          </div>
        </div>
        <div>
          <div className="font-medium text-text-primary text-sm flex items-center gap-2">
            {symA} / {symB}
            {isDJNNPair && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/20">DJNN</span>
            )}
          </div>
          <div className="text-xs text-text-tertiary mt-0.5 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium">
              {badge}
            </span>
            <span>Earn trading fees</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {showHooks && (
          <div className="hidden sm:flex items-center gap-1">
            <HookSelector />
          </div>
        )}
        <div className="text-right hidden lg:block">
          <div className="text-xs text-primary font-medium">V4 Infinity</div>
          <div className="text-xs text-text-tertiary">Up to 4000x efficiency</div>
        </div>
        <button onClick={onAddLiquidity} className="btn-primary py-1.5 px-3 text-sm flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </div>
    </div>
  );
}

// ─── Main PoolsPage ───────────────────────────────────────────────────────────
export function PoolsPage() {
  const navigate = useNavigate();
  const [poolTab, setPoolTab] = useState<PoolTab>("v2");

  const { data: pairCount, isLoading: isPairCountLoading } = useReadContract({
    address: ADDRESSES.V2_FACTORY,
    abi: V2_FACTORY_ABI,
    functionName: "allPairsLength",
    query: { staleTime: 30_000 },
  });

  const count = pairCount ? Math.min(Number(pairCount as bigint), 20) : 0;

  const pairIndexCalls = Array.from({ length: count }, (_, i) => ({
    address: ADDRESSES.V2_FACTORY as Address,
    abi: V2_FACTORY_ABI,
    functionName: "allPairs" as const,
    args: [BigInt(i)] as const,
  }));

  const { data: pairResults, isLoading: isPairsLoading } = useReadContracts({
    contracts: pairIndexCalls,
    query: { enabled: count > 0, staleTime: 30_000 },
  });

  const pairAddresses: Address[] = (pairResults ?? [])
    .filter((r) => r.status === "success" && r.result)
    .map((r) => r.result as Address);

  const POOL_TABS: Array<{ id: PoolTab; label: string; icon: typeof Droplets }> = [
    { id: "v2", label: "V2 AMM", icon: Droplets },
    { id: "cl", label: "Concentrated CL", icon: ZoomIn },
    { id: "bin", label: "Bin Liquidity", icon: BarChart2 },
  ];

  return (
    <div className="flex flex-col items-center justify-start pt-10 pb-20 px-4 min-h-[calc(100vh-4rem)]">
      <div className="text-center mb-8 max-w-2xl">
        <h1 className="font-display text-3xl font-bold gradient-text mb-2">Pool Explorer</h1>
        <p className="text-text-secondary text-sm">
          Browse all active liquidity pools on Jasmy Swap. Add liquidity to earn trading fees.
        </p>
      </div>

      <div className="w-full max-w-3xl mb-6">
        <div className="flex gap-1 p-1 bg-surface/50 rounded-card">
          {POOL_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setPoolTab(id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-button text-sm font-medium transition-all",
                poolTab === id
                  ? "bg-primary/15 text-primary"
                  : "text-text-secondary hover:text-text-primary"
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full max-w-3xl space-y-3">
        {poolTab === "v2" && (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-medium text-text-secondary text-sm">
                V2 Pools ({pairAddresses.length}{count < Number(pairCount ?? 0) ? "+" : ""})
              </h2>
              <button onClick={() => navigate("/liquidity")} className="btn-primary py-1.5 px-3 text-sm flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" />
                New Position
              </button>
            </div>

            {(isPairCountLoading || isPairsLoading) && (
              <div className="flex items-center justify-center py-12 text-text-tertiary gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading pools...</span>
              </div>
            )}

            {!isPairsLoading && pairAddresses.length === 0 && (
              <div className="text-center py-12 text-text-tertiary">
                <Droplets className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p>No V2 pools found</p>
                <button onClick={() => navigate("/liquidity")} className="btn-primary mt-4 py-1.5 px-4 text-sm">
                  Create First Pool
                </button>
              </div>
            )}

            {pairAddresses.map((addr) => (
              <V2PairRow key={addr} pairAddress={addr} onAddLiquidity={() => navigate("/liquidity")} />
            ))}
          </>
        )}

        {poolTab === "cl" && (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-medium text-text-secondary text-sm">
                Concentrated Liquidity Pools
              </h2>
              <button onClick={() => navigate("/liquidity?tab=cl")} className="btn-primary py-1.5 px-3 text-sm flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" />
                New Position
              </button>
            </div>

            <div className="p-3 bg-primary/5 border border-primary/15 rounded-button text-xs text-text-secondary mb-3">
              Concentrated Liquidity pools let you set custom price ranges.
              Hook column shows V4 hooks active on each pool — custom hooks coming soon.
            </div>

            {KNOWN_PAIRS.map((pair) =>
              CL_FEE_TIERS.map((tier) => (
                <V4PoolRow
                  key={`cl-${pair.tokenA.symbol}-${pair.tokenB.symbol}-${tier.fee}`}
                  tokenA={pair.tokenA}
                  tokenB={pair.tokenB}
                  badge={`CL ${tier.label}`}
                  onAddLiquidity={() => navigate("/liquidity?tab=cl")}
                  showHooks
                />
              ))
            ).flat()}
          </>
        )}

        {poolTab === "bin" && (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-medium text-text-secondary text-sm">
                Bin Liquidity Pools (Liquidity Book)
              </h2>
              <button onClick={() => navigate("/liquidity?tab=bin")} className="btn-primary py-1.5 px-3 text-sm flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" />
                New Position
              </button>
            </div>

            <div className="p-3 bg-primary/5 border border-primary/15 rounded-button text-xs text-text-secondary mb-3">
              Bin pools provide extreme price precision. Zero slippage within a bin.
            </div>

            {KNOWN_PAIRS.map((pair) =>
              BIN_STEPS.map((step) => (
                <V4PoolRow
                  key={`bin-${pair.tokenA.symbol}-${pair.tokenB.symbol}-${step.binStep}`}
                  tokenA={pair.tokenA}
                  tokenB={pair.tokenB}
                  badge={`BIN ${step.label}`}
                  onAddLiquidity={() => navigate("/liquidity?tab=bin")}
                />
              ))
            ).flat()}
          </>
        )}
      </div>
    </div>
  );
}
